// src/pages/PrivacyPolicyPage.js
import React from 'react';
import { Container, Card } from 'react-bootstrap';

const PrivacyPolicyPage = () => {
	const lastUpdated = "October 26, 2025";

	return (
		<div className="privacy-policy-page">
			<Container className="py-5">
				<div className="text-center mb-5">
					<h1 className="text-white display-4 mb-3">Privacy Policy</h1>
					<p className="lead text-white">
						Banking Intelligence is committed to protecting your privacy and financial data
					</p>
					<p className="text-white">
						<small>Last updated: {lastUpdated}</small>
					</p>
				</div>

				<Card className="shadow-sm">
					<Card.Body className="p-5">
						<div className="privacy-content">
							{/* Introduction */}
							<section className="mb-5">
								<h2 className="h3 mb-3">1. Introduction</h2>
								<p>
									Welcome to Banking Intelligence ("we," "our," or "us"). This Privacy Policy explains how we collect,
									use, disclose, and safeguard your information when you use our AI-powered financial insights platform
									and related services (the "Service").
								</p>
								<p>
									By using our Service, you agree to the collection and use of information in accordance with this policy.
									We will not use or share your information except as described in this Privacy Policy.
								</p>
							</section>

							{/* Information We Collect */}
							<section className="mb-5">
								<h2 className="h3 mb-3">2. Information We Collect</h2>

								<h4 className="h5 mb-2">2.1 Personal Information</h4>
								<p>We may collect the following types of personal information:</p>
								<ul>
									<li><strong>Account Information:</strong> Name, email address, phone number</li>
									<li><strong>Authentication Data:</strong> Login credentials, two-factor authentication tokens</li>
									<li><strong>Profile Information:</strong> User preferences, settings, and configuration data</li>
								</ul>

								<h4 className="h5 mb-2 mt-4">2.2 Financial Information</h4>
								<p>Through our integration with Plaid and other financial service providers, we may access:</p>
								<ul>
									<li><strong>Account Data:</strong> Bank account information, balances, account types</li>
									<li><strong>Transaction Data:</strong> Transaction history, amounts, dates, merchant information</li>
									<li><strong>Identity Information:</strong> Account holder names, addresses associated with accounts</li>
									<li><strong>Income Information:</strong> Salary deposits, income patterns, employment data</li>
								</ul>
								<p className="text-muted">
									<small>
										<strong>Important:</strong> We never store your banking login credentials. All bank connections
										are established through secure, encrypted connections via Plaid's industry-standard security protocols.
									</small>
								</p>

								<h4 className="h5 mb-2 mt-4">2.3 Usage Information</h4>
								<ul>
									<li><strong>Service Usage:</strong> Features used, queries made, insights requested</li>
									<li><strong>Technical Data:</strong> IP address, browser type, device information, session data</li>
									<li><strong>AI Interaction Data:</strong> Questions asked, responses provided, feedback given</li>
								</ul>
							</section>

							{/* How We Use Your Information */}
							<section className="mb-5">
								<h2 className="h3 mb-3">3. How We Use Your Information</h2>
								<p>We use collected information for the following purposes:</p>

								<h4 className="h5 mb-2">3.1 Service Provision</h4>
								<ul>
									<li>Provide personalized financial insights and analysis</li>
									<li>Generate AI-powered recommendations and alerts</li>
									<li>Maintain and improve our AI models and algorithms</li>
									<li>Process your financial data to deliver requested services</li>
								</ul>

								<h4 className="h5 mb-2 mt-4">3.2 Account Management</h4>
								<ul>
									<li>Create and maintain your user account</li>
									<li>Authenticate your identity and prevent fraud</li>
									<li>Provide customer support and respond to inquiries</li>
									<li>Send important service notifications and updates</li>
								</ul>

								<h4 className="h5 mb-2 mt-4">3.3 Service Improvement</h4>
								<ul>
									<li>Analyze usage patterns to improve our services</li>
									<li>Develop new features and capabilities</li>
									<li>Monitor service performance and reliability</li>
									<li>Conduct research and analytics (in aggregated, anonymized form)</li>
								</ul>
							</section>

							{/* AI and Machine Learning */}
							<section className="mb-5">
								<h2 className="h3 mb-3">4. AI and Machine Learning Processing</h2>
								<p>
									Banking Intelligence uses artificial intelligence and machine learning technologies to analyze your
									financial data and provide insights. Here's how this works:
								</p>
								<ul>
									<li>Your financial data is processed by AI models to identify spending patterns, trends, and opportunities</li>
									<li>Machine learning algorithms analyze your transaction history to provide personalized recommendations</li>
									<li>AI-powered natural language processing enables conversational interactions with your financial data</li>
									<li>All AI processing is performed securely within our protected infrastructure</li>
									<li>Individual transaction details are not used to train general AI models shared across users</li>
								</ul>
							</section>

							{/* Data Sharing */}
							<section className="mb-5">
								<h2 className="h3 mb-3">5. Information Sharing and Disclosure</h2>
								<p>
									We do not sell, trade, or rent your personal information to third parties. We may share your
									information only in the following circumstances:
								</p>

								<h4 className="h5 mb-2">5.1 Service Providers</h4>
								<ul>
									<li><strong>Plaid:</strong> For secure bank account connections and transaction data retrieval</li>
									<li><strong>Cloud Infrastructure:</strong> Secure data storage and processing services</li>
									<li><strong>Analytics Providers:</strong> For service improvement (anonymized data only)</li>
								</ul>

								<h4 className="h5 mb-2 mt-4">5.2 Legal Requirements</h4>
								<p>We may disclose information when required by law or to:</p>
								<ul>
									<li>Comply with legal obligations or court orders</li>
									<li>Protect our rights, property, or safety</li>
									<li>Prevent fraud or security threats</li>
									<li>Cooperate with law enforcement investigations</li>
								</ul>

								<h4 className="h5 mb-2 mt-4">5.3 Business Transfers</h4>
								<p>
									In the event of a merger, acquisition, or sale of assets, your information may be transferred
									as part of that transaction, subject to the same privacy protections.
								</p>
							</section>

							{/* Data Security */}
							<section className="mb-5">
								<h2 className="h3 mb-3">6. Data Security</h2>
								<p>We implement robust security measures to protect your information:</p>
								<ul>
									<li><strong>Encryption:</strong> All data is encrypted in transit and at rest using industry-standard encryption</li>
									<li><strong>Access Controls:</strong> Strict access controls limit who can view your data</li>
									<li><strong>Monitoring:</strong> Continuous monitoring for security threats and anomalies</li>
									<li><strong>Regular Audits:</strong> Regular security assessments and compliance reviews</li>
									<li><strong>Secure Infrastructure:</strong> Enterprise-grade security controls and practices</li>
								</ul>
								<p className="text-muted">
									<small>
										While we strive to protect your information, no method of transmission over the internet
										or electronic storage is 100% secure. We cannot guarantee absolute security.
									</small>
								</p>
							</section>

							{/* Data Retention */}
							<section className="mb-5">
								<h2 className="h3 mb-3">7. Data Retention</h2>
								<p>We retain your information for as long as necessary to provide our services and comply with legal obligations:</p>
								<ul>
									<li><strong>Account Data:</strong> Retained while your account is active</li>
									<li><strong>Transaction Data:</strong> Retained according to your data retention preferences (default: 2 years)</li>
									<li><strong>AI Insights:</strong> Retained according to your preferences (default: 1 year)</li>
									<li><strong>Usage Data:</strong> Typically retained for 90 days for operational purposes</li>
								</ul>
								<p>
									You can modify your data retention preferences in your account settings or request data deletion
									by contacting us.
								</p>
							</section>

							{/* Your Rights */}
							<section className="mb-5">
								<h2 className="h3 mb-3">8. Your Privacy Rights</h2>
								<p>You have the following rights regarding your personal information:</p>
								<ul>
									<li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
									<li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
									<li><strong>Deletion:</strong> Request deletion of your personal information</li>
									<li><strong>Portability:</strong> Request transfer of your data in a machine-readable format</li>
									<li><strong>Restriction:</strong> Request restriction of processing in certain circumstances</li>
									<li><strong>Objection:</strong> Object to processing of your personal information</li>
									<li><strong>Withdrawal:</strong> Withdraw consent for data processing where applicable</li>
								</ul>
								<p>
									To exercise these rights, please contact us using the information provided in the "Contact Us" section.
								</p>
							</section>

							{/* Third-Party Services */}
							<section className="mb-5">
								<h2 className="h3 mb-3">9. Third-Party Services</h2>
								<p>Our Service integrates with third-party services that have their own privacy policies:</p>
								<ul>
									<li><strong>Plaid:</strong> Financial account connection service. View <a href="https://plaid.com/legal/#consumers" target="_blank" rel="noopener noreferrer">Plaid's Privacy Policy</a></li>
									<li><strong>Financial institutions:</strong> Your banks and credit card companies have their own privacy policies</li>
								</ul>
								<p>
									We encourage you to review the privacy policies of any third-party services you use in connection
									with Banking Intelligence.
								</p>
							</section>

							{/* Cookies */}
							<section className="mb-5">
								<h2 className="h3 mb-3">10. Cookies and Tracking Technologies</h2>
								<p>We use cookies and similar technologies to:</p>
								<ul>
									<li>Remember your preferences and settings</li>
									<li>Maintain your login session</li>
									<li>Analyze service usage and performance</li>
									<li>Provide security features and fraud detection</li>
								</ul>
								<p>
									You can control cookie settings through your browser preferences. Note that disabling cookies
									may affect the functionality of our Service.
								</p>
							</section>

							{/* International Transfers */}
							<section className="mb-5">
								<h2 className="h3 mb-3">11. International Data Transfers</h2>
								<p>
									Your information may be processed and stored in countries other than your country of residence.
									We ensure that any international transfers comply with applicable data protection laws and include
									appropriate safeguards to protect your information.
								</p>
							</section>

							{/* Children's Privacy */}
							<section className="mb-5">
								<h2 className="h3 mb-3">12. Children's Privacy</h2>
								<p>
									Our Service is not intended for use by children under the age of 18. We do not knowingly collect
									personal information from children under 18. If you become aware that a child has provided us with
									personal information, please contact us immediately.
								</p>
							</section>

							{/* Changes to Policy */}
							<section className="mb-5">
								<h2 className="h3 mb-3">13. Changes to This Privacy Policy</h2>
								<p>
									We may update this Privacy Policy from time to time. We will notify you of any significant changes by:
								</p>
								<ul>
									<li>Posting the new Privacy Policy on this page</li>
									<li>Sending you an email notification</li>
									<li>Providing notice through our Service</li>
								</ul>
								<p>
									Your continued use of the Service after any changes indicates your acceptance of the updated Privacy Policy.
								</p>
							</section>

							{/* Contact */}
							<section className="mb-5">
								<h2 className="h3 mb-3">14. Contact Us</h2>
								<p>If you have any questions about this Privacy Policy or our privacy practices, please contact us:</p>
								<div className="contact-info mt-3">
									<p><strong>Email:</strong> privacy@bankingintelligence.com</p>
									<p><strong>Address:</strong> Banking Intelligence<br />
										Privacy Officer<br />
										[Your Company Address]</p>
								</div>
								<p className="mt-3">
									We will respond to your inquiry within 30 days of receipt.
								</p>
							</section>
						</div>
					</Card.Body>
				</Card>
			</Container>
		</div>
	);
};

export default PrivacyPolicyPage;